import { DecimalPipe } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TableRowExpandEvent } from 'primeng/table';
import { lastValueFrom, map } from 'rxjs';
import {
  PageableRequest,
  ShippingOrderControllerService,
  ShippingOrderRequestDetail,
  ShippingOrderRequestItem,
  ShopOrderRequest
} from 'src/app/admin-api';
import {
  DropdownFilter,
  FilterChangedEvent,
  PageContent,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import {
  EditionResendSummary,
  ResendDuplicityService
} from './resend-duplicity.service';

@Component({
  selector: 'app-resend',
  templateUrl: './resend.component.html',
  styleUrl: './resend.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [MessageService, DecimalPipe]
})
export class ResendComponent implements OnInit {
  public shippingOrderService = inject(ShippingOrderControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private decimalPipe = inject(DecimalPipe);
  private messageService = inject(MessageService);
  private title = inject(Title);
  private router = inject(Router);
  private resendDuplicityService = inject(ResendDuplicityService);

  @ViewChild(TableComponent) table: TableComponent;

  dropdownFilters: { [key: string]: Array<DropdownFilter> } = {};
  colsAvailable: Array<
    TableColumn & { requestType?: 'subscription' | 'shop' }
  > = [
    {
      ...new TableColumn.Builder()
        .setHeader('')
        .setField(' ')
        .setType('checkbox')
        .setFilter(false)
        .setSortable(false)
        .build(),
      requestType: 'subscription'
    },
    new TableColumn.Builder()
      .setHeader('ID')
      .setField('shippingOrderRequestId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('status')
      .setType('number')
      .setDisplayFunction(
        (request: ShippingOrderRequestDetail) => request.status
      )
      .setCondition('in')
      .build(),
    new TableColumn.Builder()
      .setHeader('Assinante')
      .setField('personName')
      .setCondition('contains')
      .setRouterLink('/users/person/')
      .setRouterLinkFieldName('personId')
      .build(),
    new TableColumn.Builder()
      .setHeader('CPF assinante')
      .setField('cpf')
      .setType('inputMaskNumber')
      .setMask('999.999.999-99')
      .setRouterLink('/users/person/')
      .setRouterLinkFieldName('personId')
      .setDisplayFunction((request: ShippingOrderRequestDetail) =>
        request.cpf ? FormUtil.cpfFormatted(request.cpf) : null
      )
      .build(),
    new TableColumn.Builder().setHeader('Email').setField('email').build(),
    new TableColumn.Builder().setHeader('Glam ID').setField('glamId').build(),
    new TableColumn.Builder()
      .setHeader('Id do Ticket')
      .setField('ticketId')
      .setType('number')
      .setRouterLink('https://b4asupport.zendesk.com/agent/tickets/')
      .setRouterLinkFieldName('ticketId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Detalhes do Ticket')
      .setField('ticketDetail')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Origem do Ticket')
      .setField('ticketOrigin')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Allow duplicidade')
      .setField('allowDuplicity')
      .setType('boolean')
      .build(),
    {
      ...new TableColumn.Builder()
        .setHeader('Edição')
        .setField('theme')
        .setCondition('contains')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Edição')
        .setField('editionName')
        .setCondition('contains')
        .build(),
      requestType: 'shop'
    },
    new TableColumn.Builder()
      .setHeader('Criação')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .build(),
    {
      ...new TableColumn.Builder()
        .setHeader('Pedido de reenvio')
        .setField('shippingOrderIdResend')
        .setType('number')
        .setRouterLink('/products/shipping-orders/')
        .setRouterLinkFieldName('shippingOrderIdResend')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Pedido de reenvio')
        .setField('shopOrderIdResend')
        .setRouterLink((row: ShopOrderRequest) =>
          row.shopOrderIdResend
            ? `/shop-orders/order/${row.shopOrderIdResend.toString().trim()}`
            : null
        )
        .build(),
      requestType: 'shop'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Status reenvio')
        .setField('shippingOrderStatusNameResend')
        .setCondition('in')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Transportadora')
        .setField('companyServiceName')
        .setCondition('contains')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Transportadora')
        .setField('shippingCompanyName')
        .setCondition('contains')
        .build(),
      requestType: 'shop'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Data exportação')
        .setField('dateSyncERP')
        .setType('date')
        .setCondition('between')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Status Millennium')
        .setField('statusMillennium')
        .setCondition('contains')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Nº Nota reenvio')
        .setField('nfseResend')
        .setType('number')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Pedido original')
        .setField('shippingOrderIdOriginal')
        .setType('number')
        .setRouterLink('/products/shipping-orders/')
        .setRouterLinkFieldName('shippingOrderIdOriginal')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Pedido original')
        .setField('shopOrderIdOriginal')
        .setRouterLink('/shop-orders/order/')
        .setRouterLinkFieldName('shopOrderIdOriginal')
        .build(),
      requestType: 'shop'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Nº Nota original')
        .setField('nfse')
        .setType('number')
        .build(),
      requestType: 'subscription'
    },
    {
      ...new TableColumn.Builder()
        .setHeader('Status no Ticket')
        .setField('ticketShippingStatus')
        .setType('text')
        .build(),
      requestType: 'subscription'
    },
    new TableColumn.Builder()
      .setHeader('Mensagem de Erro')
      .setField('errorMessage')
      .setCondition('contains')
      .build()
  ];

  cols = this.colsAvailable.filter(
    (c) => !c.requestType || c.requestType === 'subscription'
  );

  shopCols = this.colsAvailable.filter(
    (c) => !c.requestType || c.requestType === 'shop'
  );

  productCols = [
    new TableColumn.Builder()
      .setHeader('Imagem')
      .setField('defaultImageURL')
      .setType('image')
      .setFilter(false)
      .setStyleClass('product-image')
      .build(),
    new TableColumn.Builder()
      .setHeader('Produto')
      .setField('productVariantName')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('SKU Admin')
      .setField('productVariantId')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('SKU Millennium')
      .setField('externalId')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('EAN')
      .setField('internalEAN')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Quantidade')
      .setField('quantity')
      .setType('formattedInteger')
      .build(),
    new TableColumn.Builder()
      .setHeader('Estoque')
      .setField('inStockAcquisition')
      .setType('formattedInteger')
      .build()
  ];
  colsReview = this.colsAvailable.filter(
    (c) =>
      c.type !== 'checkbox' &&
      (!c.requestType || c.requestType === 'subscription')
  );

  status: WritableSignal<number> = signal(0);
  showModal = false;
  selectionList: ShippingOrderRequestDetail[];
  tab = 0;

  /** Reenvios já existentes nas edições selecionadas (exceto os selecionados). */
  duplicateResends: EditionResendSummary[] = [];
  /** Exige confirmação humana explícita quando há duplicidade. */
  confirmDuplicatesControl = new FormControl<boolean>(false);

  duplicateCols = [
    new TableColumn.Builder()
      .setHeader('Edição (assinante)')
      .setField('subscriberEditionId')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Pedido de reenvio')
      .setField('shippingOrderIdResend')
      .setType('number')
      .setRouterLink('/products/shipping-orders/')
      .setRouterLinkFieldName('shippingOrderIdResend')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status solicitação')
      .setField('requestStatusName')
      .build(),
    new TableColumn.Builder()
      .setHeader('Status reenvio')
      .setField('shippingOrderStatusName')
      .build(),
    new TableColumn.Builder()
      .setHeader('Origem do ticket')
      .setField('ticketOrigin')
      .build(),
    new TableColumn.Builder()
      .setHeader('Ticket')
      .setField('ticketId')
      .setType('number')
      .setRouterLink('https://b4asupport.zendesk.com/agent/tickets/')
      .setRouterLinkFieldName('ticketId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Criação')
      .setField('dateCreated')
      .setType('date')
      .build()
  ];

  get hasDuplicates(): boolean {
    return this.duplicateResends.length > 0;
  }

  /**
   * Origem TSimples: canal automático de falha de entrega e principal fonte de
   * duplicidade acidental. Reenvios dessa origem sempre exigem confirmação
   * consciente do operador (forçar duplicidade) para serem aprovados.
   */
  get hasTSimplesOrigin(): boolean {
    return (this.selectionList ?? []).some(
      (item) => (item.ticketOrigin ?? '').trim().toLowerCase() === 'tsimples'
    );
  }

  /** Aprovação exige forçar duplicidade quando há duplicata OU origem TSimples. */
  get requiresDuplicityConfirmation(): boolean {
    return this.hasDuplicates || this.hasTSimplesOrigin;
  }

  async ngOnInit() {
    this.title.setTitle('Solicitações de Reenvio');
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['tab'] && !Number.isNaN(params['tab'])) {
        this.tab = Number(params['tab']);
      } else {
        this.tab = 0;
      }
    });
    await Promise.all([
      this.findShippingOrderTypes(),
      this.findRequestStatusList(),
      this.findShippingOrderStatusList()
    ]);
  }

  findPage = async (
    request: PageableRequest
  ): Promise<PageContent<ShippingOrderRequestDetail>> => {
    const type = request.filters?.find(
      (f) => f.field === 'shippingOrderTypeName'
    );
    if (type?.value) {
      if (!Array.isArray(type.value)) {
        type.value = [];
      }
      request.filters?.push({
        condition: 'in',
        field: 'shippingOrderTypeId',
        fieldType: 'number',
        value: type.value
      });
    }
    const status = request.filters?.find(
      (f) => f.field === 'shippingOrderStatusNameResend'
    );
    if (status?.value) {
      if (!Array.isArray(status.value)) {
        status.value = [];
      }
      request.filters?.push({
        condition: 'in',
        field: 'shippingOrderStatusResend',
        fieldType: 'number',
        value: status.value
      });
    }

    return lastValueFrom(
      this.shippingOrderService
        .findShippingOrderRequestTable(request)
        .pipe(
          map((data) => data.result as PageContent<ShippingOrderRequestDetail>)
        )
    );
  };

  async findShippingOrderTypes(): Promise<void> {
    this.dropdownFilters['shippingOrderTypeName'] = await lastValueFrom(
      this.shippingOrderService
        .getShippingOrderTypes()
        .pipe(map((data) => data.result))
    );
  }

  async findRequestStatusList(): Promise<void> {
    this.dropdownFilters['status'] = await lastValueFrom(
      this.shippingOrderService
        .getShippingOrderRequestStatusList()
        .pipe(map((data) => data.result))
    );
  }

  async findItems($event: TableRowExpandEvent, service: unknown) {
    const data = $event.data as ShippingOrderRequestDetail;
    if (!data.items && data.shippingOrderIdResend) {
      LoaderService.showLoader();
      const items = await lastValueFrom(
        (service as ShippingOrderControllerService)
          .findShippingOrderRequestItems(data.shippingOrderIdResend)
          .pipe(map((data) => data.result))
      );
      data.items = items;
      LoaderService.showLoader(false);
    }
  }

  persistFilters($event: FilterChangedEvent) {
    if ($event.field === 'status') this.status.set($event.value as number);
  }

  productBackground = (
    item: ShippingOrderRequestDetail | ShippingOrderRequestItem
  ): string => {
    // Se a variavel item é uma instancia de ShippingOrderRequestItem
    if (
      Object.prototype.hasOwnProperty.call(item, 'productVariantId') &&
      (item as ShippingOrderRequestItem).shippingOrderStatus === 15 && //AG. ESTOQUE
      (item as ShippingOrderRequestItem).inStockAcquisition < 0
    ) {
      return '#ef444478';
    }
    return null;
  };

  rowSelectable = (item: {
    data: ShippingOrderRequestDetail | ShippingOrderRequestItem;
  }): boolean => {
    return (
      Object.prototype.hasOwnProperty.call(
        item.data,
        'shippingOrderIdResend'
      ) &&
      (item.data as ShippingOrderRequestDetail).status ===
        ShippingOrderRequestDetail.StatusEnum.PENDENTE &&
      (item.data as ShippingOrderRequestDetail).shippingOrderStatusResend === 14 // AG. APROVAÇÃO
    );
  };

  hasChildren = (item: ShippingOrderRequestDetail): boolean => {
    return item.shippingOrderIdResend > 0 || item.items?.length > 0;
  };

  async findShippingOrderStatusList(): Promise<void> {
    this.dropdownFilters['shippingOrderStatusName'] = await lastValueFrom(
      this.shippingOrderService.getShippingOrderStatusList().pipe(
        map((data) => {
          data.result.sort((a, b) =>
            a.shippingOrderStatusName.localeCompare(b.shippingOrderStatusName)
          );
          return data.result.map(
            (status) =>
              ({
                label: status.shippingOrderStatusName,
                value: status.shippingOrderStatusId
              }) as DropdownFilter
          );
        })
      )
    );
    this.dropdownFilters['shippingOrderStatusNameResend'] = [
      ...this.dropdownFilters['shippingOrderStatusName']
    ];
  }

  async validateResendOrders(
    list: ShippingOrderRequestDetail[]
  ): Promise<void> {
    this.selectionList = [...list];
    this.confirmDuplicatesControl.setValue(false);
    this.showModal = true;
    await this.loadEditionResends();
  }

  /**
   * Carrega os reenvios já existentes nas edições selecionadas e destaca os que
   * NÃO estão sendo aprovados agora e não foram cancelados — sinalizando
   * duplicidade para uma decisão humana consciente antes de aprovar.
   */
  private async loadEditionResends(): Promise<void> {
    this.duplicateResends = [];
    const selectedRequestIds = new Set(
      this.selectionList.map((item) => item.shippingOrderRequestId)
    );
    const editionIds = Array.from(
      new Set(
        this.selectionList
          .map((item) => item.subscriberEditionId)
          .filter((id): id is number => !!id)
      )
    );
    if (!editionIds.length) return;
    try {
      LoaderService.showLoader();
      const results = await Promise.all(
        editionIds.map((editionId) =>
          lastValueFrom(this.resendDuplicityService.getEditionResends(editionId))
        )
      );
      const CANCELADO = 3;
      this.duplicateResends = results
        .flat()
        .filter(
          (resend) =>
            resend.requestStatus !== CANCELADO &&
            !selectedRequestIds.has(resend.shippingOrderRequestId)
        );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async approveResendOrders(): Promise<void> {
    if (this.requiresDuplicityConfirmation && !this.confirmDuplicatesControl.value) {
      AppDialogService.showInfoDialog({
        message: this.hasTSimplesOrigin
          ? 'Há reenvio(s) de origem TSimples na seleção. Reenvios do TSimples exigem ' +
            'confirmação de duplicidade. Revise os reenvios e marque a confirmação para aprovar.'
          : 'Esta(s) edição(ões) já possui(em) outros reenvios não cancelados. ' +
            'Revise a lista de duplicidades e marque a confirmação para aprovar mesmo assim.',
        header: 'Confirmação de duplicidade necessária',
        visible: true
      });
      return;
    }
    try {
      LoaderService.showLoader();
      const orders = await lastValueFrom(
        this.resendDuplicityService.approveResends(
          this.selectionList.map((item) => item.shippingOrderRequestId),
          this.requiresDuplicityConfirmation &&
            !!this.confirmDuplicatesControl.value
        )
      );
      const approved = orders?.filter((o) => o.shippingOrderStatus === 0);
      const rejected = orders?.filter((o) => o.shippingOrderStatus === 15);
      if (approved?.length)
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `${approved.length} pedido(s) de reenvio aprovado(s).`
        });
      if (rejected?.length)
        AppDialogService.showInfoDialog({
          message: `Infelizmente o estoque de alguns itens se esgotou enquanto o pedido aguardava aprovação, e ${this.decimalPipe.transform(rejected.length, '1.0-0')} pedido(s) voltaram para "Ag.Estoque".`,
          header: 'Produtos esgotados',
          visible: true
        });
      await this.table.refresh(true);
      this.showModal = false;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async rejectResendOrders(): Promise<void> {
    try {
      LoaderService.showLoader();
      const orders = await lastValueFrom(
        this.shippingOrderService
          .rejectResendOrders(
            this.selectionList.map((item) => item.shippingOrderRequestId)
          )
          .pipe(map((data) => data.result))
      );
      if (orders?.length) {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `${orders.length} pedido(s) de reenvio rejeitado(s).`
        });
      }
      await this.table.refresh(true);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async recalculateStocks(): Promise<void> {
    try {
      LoaderService.showLoader();
      const result = await lastValueFrom(
        this.shippingOrderService
          .refreshShippingOrderRequestStocks()
          .pipe(map((data) => data.result))
      );
      if (result?.length)
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `${result?.length} pedidos com estoque disponível.`
        });
      await this.table.refresh(true);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  navigate(tab?: number) {
    if (tab !== undefined) this.tab = tab;
    this.router.navigate(['/operations/resend'], { queryParams: { tab } });
  }
}
